import React, { ReactNode, useEffect } from 'react';
import {
  IContact,
  IUserSuite,
} from 'src/services/api/ServiceRequestDashboard/apiContracts';
import CategoryPill from 'src/components/CategoryPill/CategoryPill';
import { SuiteListItemContactForm } from 'src/components/ContactForm/types';
import './contact-property-dialogue.scss';

interface CloseIconProps {
  width?: number;
  height?: number;
  className?: string;
}

export const CloseIcon = ({
  width = 20,
  height = 20,
  className = '',
}: CloseIconProps) => {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 15 15"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M11.7816 4.03157C12.0062 3.80702 12.0062 3.44295 11.7816 3.2184C11.5571 2.99385 11.193 2.99385 10.9685 3.2184L7.50005 6.68682L4.03164 3.2184C3.80708 2.99385 3.44301 2.99385 3.21846 3.2184C2.99391 3.44295 2.99391 3.80702 3.21846 4.03157L6.68688 7.49999L3.21846 10.9684C2.99391 11.193 2.99391 11.557 3.21846 11.7816C3.44301 12.0061 3.80708 12.0061 4.03164 11.7816L7.50005 8.31316L10.9685 11.7816C11.193 12.0061 11.5571 12.0061 11.7816 11.7816C12.0062 11.557 12.0062 11.193 11.7816 10.9684L8.31322 7.49999L11.7816 4.03157Z"
        fill="currentColor"
        fillRule="evenodd"
        clipRule="evenodd"
      />
    </svg>
  );
};

// Consolidate the two separate interface blocks into one.
interface UserSuitesDisplayProps {
  /* TODO: HACK: Allowing SuiteListItemContactForm[] to eliminate TS errors
   * until the data model is harmonized. */
  userSuites: IUserSuite[] | SuiteListItemContactForm[] | string | null;
  properties?: Array<{
    propertyId: string;
    name: string;
  }> | null;
}

const UserSuitesDisplay = ({
  userSuites,
  properties,
}: UserSuitesDisplayProps) => {
  // Type guard to check if userSuites is an array of IUserSuite
  const isUserSuitesArray = (
    suites: IUserSuite[] | SuiteListItemContactForm[] | string | null
  ): suites is IUserSuite[] => {
    return Array.isArray(suites);
  };

  // Priority 1: Show user suites if available (Home Owners/Occupants).
  if (isUserSuitesArray(userSuites) && userSuites.length > 0) {
    return (
      <div className="suites-list">
        {userSuites.map((suite: IUserSuite, index: number) => (
          <div key={`${suite.suiteId}-${index}`} className="suite-card">
            <div className="suite-header">
              <h4 className="suite-property">{suite.propertyName}</h4>
              <CategoryPill
                text={`Suite ${suite.suiteName}`}
                size="small"
                color="#1976d2"
                textColor="white"
              />
            </div>
            <div className="suite-details">
              <div className="suite-detail-row">
                <span className="detail-label">Building:</span>
                <span className="detail-value">
                  {suite.buildingName !== 'N/A'
                    ? suite.buildingName
                    : 'Not specified'}
                </span>
              </div>
              <div className="suite-detail-row">
                <span className="detail-label">Level:</span>
                <span className="detail-value">
                  {suite.levelName !== 'N/A'
                    ? suite.levelName
                    : 'Not specified'}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Priority 2: Show properties if available (Property Developers/Managers).
  if (properties && properties.length > 0) {
    return (
      <div className="suites-list">
        {properties.map((property, index) => (
          <div key={`${property.propertyId}-${index}`} className="suite-card">
            <div className="suite-header">
              <h4 className="suite-property">{property.name}</h4>
              <CategoryPill
                text="Managed Property"
                size="small"
                color="#1976d2"
                textColor="white"
              />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (typeof userSuites === 'string') {
    return (
      <div className="no-suites">
        <p>{userSuites}</p>
      </div>
    );
  }

  return (
    <div className="no-suites">
      <p>No property assignments found for this contact.</p>
    </div>
  );
};

interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
}

const ContactDialogue = ({
  isOpen,
  onClose,
  title,
  description,
  children,
}: DialogProps) => {
  // Close dialog when clicking outside or pressing escape key
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-container">
        <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
          <div className="dialog-header">
            <div>
              <h2 className="dialog-title">{title}</h2>
              {description && (
                <p className="dialog-description">{description}</p>
              )}
            </div>
            <button
              className="dialog-close"
              onClick={onClose}
              aria-label="Close"
            >
              <CloseIcon />
            </button>
          </div>
          <div className="modern-dialog-body">{children}</div>
        </div>
      </div>
    </div>
  );
};

interface ContactPropertyDetailsDialogueProps {
  isOpen: boolean;
  onClose: () => void;
  contactData: IContact | null;
}

export const ContactPropertyDetailsDialogue = ({
  isOpen,
  onClose,
  contactData,
}: ContactPropertyDetailsDialogueProps) => {
  if (!contactData) return null;

  const { firstname, lastname, userSuites, properties }: IContact = contactData;

  return (
    <ContactDialogue
      isOpen={isOpen}
      onClose={onClose}
      title={`${firstname} ${lastname}`}
      description="Contact property details"
    >
      <div className="contact-property-details">
        <div className="property-section">
          <h3 className="section-title">Property Details</h3>
          <UserSuitesDisplay userSuites={userSuites} properties={properties} />
        </div>
      </div>
    </ContactDialogue>
  );
};

export default ContactPropertyDetailsDialogue;
